//components/FormSettings/FormPreview.jsx
// Mar1 FormPreview without FormSubmissionsContext integration:
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useFormContext } from "../../contexts/FormContext";
import { useFieldMetadata } from "../../contexts/FieldMetadataContext";
import { useFormTheme } from "../../contexts/FormThemeContext";
import { useFormLayout } from "../../contexts/FormLayoutContext";
import { Paper, Box, ThemeProvider, Typography } from "@mui/material";
import PreviewFields from "./PreviewFields"; // ✅ Import the new PreviewFields component
import SubmitButtonComponent from "../FormSettings/FormDesignLayout/SubmitButtonComponent"; // ✅ Import Submit Button
import { useFormSubmissionSettings } from "../../contexts/FormSubmissionSettingsContext"; // Import submission settings context
import { replaceChipsWithValues } from "../../utils/formUtils"; // ✅ Import chip replacement helper
import FormPreviewSkeleton from "./FormPreviewSkeleton"; // 🔹 Import our new Skeleton component
import { useFormSubmissions } from "../../contexts/FormSubmissionsContext";

import DisplayCardPreview  from "./FormSubmitSettings/DisplayCardPreview";
import DisplayTablePreview from "./FormSubmitSettings/DisplayTablePreview";

/**
 * FormPreviewTest renders a full preview of the form using the MUI theme from FormThemeContext.
 * It waits for:
 *   - formId to become ready (formIdReady === true)
 *   - form metadata to load (metadataLoading === false)
 *   - layout to load (isLoading === false)
 * If any are not ready, it shows a skeleton instead.
 */
const FormPreviewTest = () => {
  const { formId, formIdReady } = useFormContext();
  const { fieldsMetadata, isLoading: metadataLoading } = useFieldMetadata();
  const { layoutConfig, fetchLatestLayout, isLoading } = useFormLayout();
  const { muiTheme, selectedTheme, themeLoading, colors } = useFormTheme();
  const { getSubmissionUIComponent } = useFormSubmissionSettings();

  // Builder preview renders final submitted output inside the builder route.
  // Enterprise presentation note:
  // - FormSubmissionSettingsContext still owns which submission action renders.
  // - FormPreview owns this surface-level presentation choice because this page
  //   intentionally previews final Display Card output while still living under
  //   /form-builder.
  // - This prevents builder-route detection from disabling the outlined
  //   Generated URL field wrapper in preview mode.
  const SubmissionUI = getSubmissionUIComponent({
    renderDisplayCardOutputAsOutlinedField: true,
  });

//  const { handleSubmit, submissions, loading, submitStatus } = useFormSubmissions();
  const {
    handleSubmit,
    fetchSubmissions,     // 🆕 need this
    submissions,
    loading,
    submitStatus,
  } = useFormSubmissions();

  // Memoize submissions & status to avoid triggering re-renders
  const memoizedSubmissions = useMemo(() => submissions, [submissions]);
  const memoizedSubmitStatus = useMemo(() => submitStatus, [submitStatus]);

  // Debugging log (fires only if memoized data changes)
  useEffect(() => {
    console.log("[FormPreviewTest] SubmissionsContext Access:", {
      handleSubmitExists: typeof handleSubmit === "function",
      memoizedSubmissions,
      loading,
      memoizedSubmitStatus,
    });
  }, [memoizedSubmissions, loading, memoizedSubmitStatus]);

  /**
   * Instead of separate states, we check *both* metadataLoading and layout isLoading.
   * Only when formId is ready, metadata has loaded, and layout is loaded,
   * do we show the final form. Otherwise, we show the skeleton.
   */
  const [isReady, setIsReady] = useState(false);
  const fetchedViewerRowsRef = useRef(false);   // prevents duplicate viewer-only fetch

useEffect(() => {
  /* ------------------------------------------------------------
   * Are all the async chunks ready?
   * ---------------------------------------------------------- */
  const ready =
    formId &&
    formIdReady &&
    !metadataLoading &&
    !isLoading &&
    !themeLoading;

  /* ── not ready → stay in skeleton mode ── */
  if (!ready) {
    setIsReady(false);
    return;                // ⤺ early exit
  }

  /* ── ready → show real preview ── */
  fetchLatestLayout();     // pulls latest builder layout once
  setIsReady(true);

  /* ------------------------------------------------------------
   * First-time-only viewer-rows fetch
   * ---------------------------------------------------------- */
  if (!fetchedViewerRowsRef.current && typeof fetchSubmissions === "function") {
    fetchSubmissions(false, null, true);   // loadMore=false, sizeOverride=null, onlyMine=true
    fetchedViewerRowsRef.current = true;   // lock so we don’t fetch again
  }
}, [
  formId,
  formIdReady,
  metadataLoading,
  isLoading,
  themeLoading,
  fetchLatestLayout,
  fetchSubmissions,
]);

  /**
   * [NEW LOG] - Log whenever layoutConfig changes, to see if textAlign flips:
   */
  useEffect(() => {
    console.log("[NEW LOG] [FormPreviewTest] layoutConfig changed:", layoutConfig);
    if (!layoutConfig) return;

    // [NEW LOG] Inspect each row in layoutConfig for alignment
    Object.keys(layoutConfig).forEach((rowKey) => {
      const rowData = layoutConfig[rowKey];
      if (Array.isArray(rowData)) {
        rowData.forEach((field, idx) => {
          if (field && field.type === "header") {
            console.log(
              `[NEW LOG] [FormPreviewTest] layoutConfig Row "${rowKey}" Index ${idx} ` +
              `Header Field ID: ${field.id} - textAlign: ${field?.style?.textAlign || field.textAlign}`
            );
          }
        });
      }
    });
  }, [layoutConfig]);

  /** 
   * 🔹 Memoize Metadata and Layout to Prevent Unnecessary Re-renders 
   * so we're not constantly re-initializing objects if fieldsMetadata or layoutConfig haven't changed.
   */
  const memoizedFieldsMetadata = useMemo(() => fieldsMetadata, [fieldsMetadata]);
  const memoizedLayoutConfig = useMemo(() => layoutConfig, [layoutConfig]);

  /**
   * 🔹 Final check: If not ready or missing data, return Skeleton
   * Ensures we don't render half-baked data or invalid forms.
   */
  if (
    !isReady ||
    !memoizedFieldsMetadata ||
    Object.keys(memoizedFieldsMetadata).length === 0 ||
    !memoizedLayoutConfig ||
    Object.keys(memoizedLayoutConfig).length === 0
  ) {
    console.warn("⏳ [FormPreviewTest] Skipping render: Data is not yet fully loaded.");
    return <FormPreviewSkeleton />;
  }

  console.log("[FormPreview] Metadata being passed to PreviewFields:", memoizedFieldsMetadata);

  // Specifically log header fields
  Object.keys(memoizedFieldsMetadata).forEach((fieldId) => {
    if (memoizedFieldsMetadata[fieldId].type === 'header') {
      console.log(
        `[FormPreview] Header Field ID: ${fieldId} - Alignment:`,
        memoizedFieldsMetadata[fieldId]?.style?.textAlign
      );
    }
  });

  console.log("✅ [FormPreviewTest] layoutConfig before rendering:", memoizedLayoutConfig);

  // [NEW LOG] - Detailed check of layoutConfig rows right before render
  if (memoizedLayoutConfig) {
    Object.keys(memoizedLayoutConfig).forEach((rowKey) => {
      const rowData = memoizedLayoutConfig[rowKey];
      if (Array.isArray(rowData)) {
        rowData.forEach((field, idx) => {
          if (field && field.type === "header") {
            console.log(
              `[NEW LOG] [FormPreviewTest] Before Render -> Row "${rowKey}" Index ${idx} ` +
              `Header Field ID: ${field.id} - textAlign: ${field?.style?.textAlign || field.textAlign}`
            );
          }
        });
      }
    });
  }

  // ------------------------------------------------------------------
  //  Robust flags – compare the *type objects* (won’t break in prod)
  // ------------------------------------------------------------------
  const isDisplayInCard = SubmissionUI && SubmissionUI.type === DisplayCardPreview;
  const isDisplayTable  = SubmissionUI && SubmissionUI.type === DisplayTablePreview;

  console.log("[FormPreviewTest] isDisplayInCard:",  isDisplayInCard);
  console.log("[FormPreviewTest] isDisplayTable:",   isDisplayTable);


  /**
   * 🔹 Actual render of the form:
   *   - ThemeProvider to apply the current MUI theme
   *   - Outer Box uses theme background color 
   *   - If "Paper" theme: we place everything in a Paper container (white).
   *     The user-selected backgroundColor remains visible behind the Paper because
   *     in FormThemeConfig, "Paper" specifically uses palette.background.paper = "#ffffff".
   *   - If "Full Page" theme: we just use a Box, effectively giving the entire page
   *     the selected background color.
   */
  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        sx={{
          backgroundColor: muiTheme.palette.background.default,
          color: muiTheme.palette.text.primary,
          fontFamily: muiTheme.typography.fontFamily,
          padding: muiTheme.spacing(1),
          width: "100%",
          minHeight: "100vh", // Ensures we see the background color fill the viewport
        }}
      >
        {selectedTheme === "Paper" ? (
          <Paper
            sx={{
              //padding: 2,
              // We rely on the Paper theme (paperTheme) from FormThemeConfig for color, shadow, etc.
              // backgroundColor: '#ffffff' by default, due to that config.
            }}
          >
            {memoizedLayoutConfig && Object.keys(memoizedLayoutConfig).length > 0 ? (
              <>
                <PreviewFields
                  fieldsMetadata={memoizedFieldsMetadata}
                  layoutConfig={memoizedLayoutConfig}
                />

                {/* ✅ Render Submit Button Separately (if present in layout) */}
                {memoizedLayoutConfig.submitRow &&
                  memoizedLayoutConfig.submitRow.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", marginTop: 1 }}>
                      <SubmitButtonComponent 
                        field={memoizedLayoutConfig.submitRow[0]}
                        onClick={() => handleSubmit()} // ✅ Calls FormSubmissionsContext when clicked
                      />
                    </Box>
                  )}

                {/* ✅ Dynamically Render Submission UI (e.g., DisplayCardPreview) */}
                {SubmissionUI && (
                  // 🔥 ADDED: If it's DisplayCardPreview, wrap it in a negative margin Box
                 (isDisplayInCard || isDisplayTable) ? (
                    <Box
                      sx={{
                        marginTop: 2,
                        mx: "-2rem", // Adjust to match your actual Paper padding from FormThemeConfig
                      }}
                    >
                      {SubmissionUI}
                    </Box>
                  ) : (
                    <Box sx={{ marginTop: 2 }}>
                      {SubmissionUI} {/* ✅ Renders DisplayCardPreview if enabled */}
                    </Box>
                  )
                )}
              </>
            ) : (
              <Typography variant="body1">No fields found in layout.</Typography>
            )}
          </Paper>
        ) : (
          <Box
            sx={{
              color: muiTheme.palette.text.primary,
              fontFamily: muiTheme.typography.fontFamily,
              padding: muiTheme.spacing(2),
              width: "100%",
              minHeight: "100vh",
            }}
          >
            <PreviewFields
              fieldsMetadata={memoizedFieldsMetadata}
              layoutConfig={memoizedLayoutConfig}
            />

            {/* ✅ Render Submit Button Separately (if present in layout) */}
            {memoizedLayoutConfig.submitRow &&
              memoizedLayoutConfig.submitRow.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
                  <SubmitButtonComponent 
                    field={memoizedLayoutConfig.submitRow[0]}
                    onClick={() => handleSubmit()} // ✅ Calls FormSubmissionsContext when clicked
                  />
                </Box>
              )}

            {/* ✅ Dynamically Render Submission UI (e.g., DisplayCardPreview) */}
            {SubmissionUI && (
              <Box sx={{ marginTop: 2 }}>
                {SubmissionUI} {/* ✅ Dynamically renders DisplayCardPreview if enabled */}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default React.memo(FormPreviewTest);
